import last from "lodash/last";
import { LandingLayoutWithLandingDescription } from "PFApp/components/landing_layout";
import { useSession } from "PFApp/use_session";
import { Alert } from "PFComponents/alert";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import api from "PFCore/api";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { FeatureFlag } from "PFTypes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router";

import css from "./forgot_password.module.scss";

export const EditForgottenPasswordPage = () => {
  const navigate = useNavigate();
  const tokenRef = useRef(
    new URLSearchParams(window.location.search).get("reset_password_token") ||
      window.storage.getItem("reset_password_token")
  );

  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: currentAccount } = useCurrentAccount();
  const isPasswordSignInEnabled = useIsFeatureEnabled(FeatureFlag.PasswordSignin);
  const isRequestAccessEnabled = useIsFeatureEnabled(FeatureFlag.RequestAccess);

  const { t } = useTranslation();
  const { signIn } = useSession();

  useEffect(() => {
    if (window.storage.getItem("reset_password_token") === null) {
      // Store token with 30 minute expiration for security
      window.storage.setItem("reset_password_token", tokenRef.current, { expiresIn: 30 * 60 });
    }
  }, []);

  // eslint-disable-next-line react-hooks/refs
  if (!tokenRef.current) {
    return <Navigate to="/" replace />;
  }

  const submit = (event) => {
    event.preventDefault(); // prevent form submit navigation
    setIsLoading(true);
    setErrors({});

    api({
      url: "auth/edit_password",
      method: "POST",
      data: {
        user: {
          accountId: currentAccount.id,
          password,
          passwordConfirmation: password,
          resetPasswordToken: tokenRef.current
        }
      },
      apiVersion: 1
    })
      .then(({ email }) => {
        // Clear the token from storage after successful reset
        window.storage.removeItem("reset_password_token");
        signIn(email, password, {}).then(() => navigate("/"));
      })
      .catch(({ response: { data } }) => {
        setIsLoading(false);
        if (data.errors.base && data.errors.base.length > 0) {
          const error = data.errors.base[0] || "";
          const translationKey = `forgotPassword.errors.${last(error.split("."))}`;
          setMessage(t(translationKey as unknown as TemplateStringsArray, { defaultValue: error }));
        } else if (data.message) {
          setMessage(data.message); // regular errors
        }
        setErrors(data.errors); // input errors
      });
  };

  return (
    <LandingLayoutWithLandingDescription>
      <strong data-qa-id="EditForgottenPasswordPage.header">{t("forgotPassword.editPassword")}</strong>

      {message && (
        <Alert className={css.marginBottom} kind="error" qaId="EditForgottenPasswordPage.successMessage">
          {message}
        </Alert>
      )}

      <form
        onSubmit={submit}
        aria-label={t("forgotPassword.editPassword")}
        data-qa-id="EditForgottenPasswordPage.form"
      >
        <InputFieldSet
          showPasswordToggle
          qaId="EditForgottenPasswordPage.password"
          label={t("forgotPassword.newPassword")}
          inputType="password"
          className={css.marginBottom}
          onChange={setPassword}
          error={errors.password}
        />

        <Button
          qaId="EditForgottenPasswordPage.submit"
          className={css.button}
          disabled={isLoading || !password.trim()}
        >
          {isLoading ? <LoadingDots /> : t("forgotPassword.changePassword")}
        </Button>
      </form>

      <div className={css.marginBottom}>
        <Link to="/" data-qa-id={"EditForgottenPasswordPage.signInLink"}>
          {t("forgotPassword.signIn")}
        </Link>
      </div>

      {isPasswordSignInEnabled && (
        <>
          <div>
            <span>{t("forgotPassword.dontHaveAccount")}</span>
          </div>
          <div>
            <Link
              data-qa-id={
                isRequestAccessEnabled
                  ? "EditForgottenPasswordPage.requestAccess"
                  : "EditForgottenPasswordPage.signUp"
              }
              to={isRequestAccessEnabled ? "/request_access" : "/users/signup"}
            >
              {t("forgotPassword.signUp")}
            </Link>
          </div>
        </>
      )}
    </LandingLayoutWithLandingDescription>
  );
};
